type CompressedSubstate = string;

export type CompressedEditorState = {
    nodes: CompressedParagraph[];
};

type CompressedParagraph = {
    nodes: CompressedNode[];
};

type CompressedNode =
    | { t: 'text'; c: string }
    | { t: 'fraction'; num: CompressedSubstate; den: CompressedSubstate }
    | { t: 'exponent'; base: CompressedSubstate; exp: CompressedSubstate }
    | { t: 'root'; rad: CompressedSubstate; idx?: CompressedSubstate }
    | { t: 'overline'; content: CompressedSubstate; variant: 'segment' | 'line' | 'ray' };

function compressSubstate(stateStr: string): CompressedSubstate {
    if (!stateStr) return '';

    try {
        const parsed = JSON.parse(stateStr);
        const compressed: CompressedNode[] = [];

        if (parsed.root?.children) {
            for (const para of parsed.root.children) {
                if (para.children) {
                    for (const node of para.children) {
                        if (node.type === 'text' && node.text) {
                            compressed.push({
                                t: 'text',
                                c: node.text,
                            });
                        } else if (node.type === 'fraction') {
                            compressed.push({
                                t: 'fraction',
                                num: compressSubstate(node.num?.state || ''),
                                den: compressSubstate(node.den?.state || ''),
                            });
                        } else if (node.type === 'exponent') {
                            compressed.push({
                                t: 'exponent',
                                base: compressSubstate(node.base?.state || ''),
                                exp: compressSubstate(node.exp?.state || ''),
                            });
                        } else if (node.type === 'root') {
                            const rootNode: any = {
                                t: 'root',
                                rad: compressSubstate(node.radicand?.state || ''),
                            };
                            if (node.index?.state) {
                                rootNode.idx = compressSubstate(node.index.state);
                            }
                            compressed.push(rootNode);
                        } else if (node.type === 'overline') {
                            compressed.push({
                                t: 'overline',
                                content: compressSubstate(node.content?.state || ''),
                                variant: node.variant || 'segment',
                            });
                        }
                    }
                }
            }
        }

        // Return as JSON string for nested structures
        return JSON.stringify(compressed);
    } catch {
        return '';
    }
}

function textToState(text: string): string {
    if (!text) {
        return JSON.stringify({
            root: {
                children: [
                    {
                        children: [],
                        direction: null,
                        format: '',
                        indent: 0,
                        type: 'paragraph',
                        version: 1,
                    },
                ],
                direction: null,
                format: '',
                indent: 0,
                type: 'root',
                version: 1,
            },
        });
    }

    return JSON.stringify({
        root: {
            children: [
                {
                    children: [
                        {
                            detail: 0,
                            format: 0,
                            mode: 'normal',
                            style: '',
                            text: text,
                            type: 'text',
                            version: 1,
                        },
                    ],
                    direction: null,
                    format: '',
                    indent: 0,
                    type: 'paragraph',
                    version: 1,
                },
            ],
            direction: null,
            format: '',
            indent: 0,
            type: 'root',
            version: 1,
        },
    });
}

function decompressSubstate(compressedSubstate: CompressedSubstate): string {
    if (!compressedSubstate) {
        return textToState('');
    }

    try {
        // Try to parse as JSON (nested structure)
        const parsed = JSON.parse(compressedSubstate);

        if (Array.isArray(parsed)) {
            // It's an array of compressed nodes - decompress recursively
            const children: any[] = [];

            for (const node of parsed) {
                if (node.t === 'text') {
                    children.push({
                        detail: 0,
                        format: 0,
                        mode: 'normal',
                        style: '',
                        text: node.c,
                        type: 'text',
                        version: 1,
                    });
                } else if (node.t === 'fraction') {
                    children.push({
                        type: 'fraction',
                        version: 1,
                        num: { state: decompressSubstate(node.num) },
                        den: { state: decompressSubstate(node.den) },
                    });
                } else if (node.t === 'exponent') {
                    children.push({
                        type: 'exponent',
                        version: 1,
                        base: { state: decompressSubstate(node.base) },
                        exp: { state: decompressSubstate(node.exp) },
                    });
                } else if (node.t === 'root') {
                    children.push({
                        type: 'root',
                        version: 1,
                        radicand: { state: decompressSubstate(node.rad) },
                        index: node.idx ? { state: decompressSubstate(node.idx) } : null,
                    });
                } else if (node.t === 'overline') {
                    children.push({
                        type: 'overline',
                        version: 1,
                        content: { state: decompressSubstate(node.content) },
                        variant: node.variant || 'segment',
                    });
                }
            }

            return JSON.stringify({
                root: {
                    children: [
                        {
                            children,
                            direction: null,
                            format: '',
                            indent: 0,
                            type: 'paragraph',
                            version: 1,
                        },
                    ],
                    direction: null,
                    format: '',
                    indent: 0,
                    type: 'root',
                    version: 1,
                },
            });
        }
    } catch {
        // Not JSON or parse failed - treat as plain text
    }

    // Fallback: treat as plain text
    return textToState(compressedSubstate);
}

export function compressEditorState(stateStr: string): string {
    if (!stateStr) return JSON.stringify({ nodes: [] });

    try {
        const parsed = JSON.parse(stateStr);
        const compressed: CompressedEditorState = { nodes: [] };

        if (parsed.root?.children) {
            for (const para of parsed.root.children) {
                const paragraphNodes: CompressedNode[] = [];

                if (para.children) {
                    for (const node of para.children) {
                        if (node.type === 'text') {
                            paragraphNodes.push({
                                t: 'text',
                                c: node.text || '',
                            });
                        } else if (node.type === 'fraction') {
                            paragraphNodes.push({
                                t: 'fraction',
                                num: compressSubstate(node.num?.state || ''),
                                den: compressSubstate(node.den?.state || ''),
                            });
                        } else if (node.type === 'exponent') {
                            paragraphNodes.push({
                                t: 'exponent',
                                base: compressSubstate(node.base?.state || ''),
                                exp: compressSubstate(node.exp?.state || ''),
                            });
                        } else if (node.type === 'root') {
                            const rootNode: any = {
                                t: 'root',
                                rad: compressSubstate(node.radicand?.state || ''),
                            };
                            if (node.index?.state) {
                                rootNode.idx = compressSubstate(node.index.state);
                            }
                            paragraphNodes.push(rootNode);
                        } else if (node.type === 'overline') {
                            paragraphNodes.push({
                                t: 'overline',
                                content: compressSubstate(node.content?.state || ''),
                                variant: node.variant || 'segment',
                            });
                        }
                    }
                }

                compressed.nodes.push({ nodes: paragraphNodes });
            }
        }

        return JSON.stringify(compressed);
    } catch (e) {
        console.error('Error compressing state:', e);
        return JSON.stringify({ nodes: [] });
    }
}

export function decompressEditorState(compressedStr: string): string {
    if (!compressedStr) {
        return emptyState();
    }

    try {
        const parsed = JSON.parse(compressedStr);

        if (parsed.nodes && Array.isArray(parsed.nodes)) {
            const paragraphs: any[] = [];

            for (const paragraph of parsed.nodes) {
                const children: any[] = [];

                if (paragraph.nodes && Array.isArray(paragraph.nodes)) {
                    for (const node of paragraph.nodes) {
                        if (node.t === 'text') {
                            children.push({
                                detail: 0,
                                format: 0,
                                mode: 'normal',
                                style: '',
                                text: node.c,
                                type: 'text',
                                version: 1,
                            });
                        } else if (node.t === 'fraction') {
                            children.push({
                                type: 'fraction',
                                version: 1,
                                num: { state: decompressSubstate(node.num) },
                                den: { state: decompressSubstate(node.den) },
                            });
                        } else if (node.t === 'exponent') {
                            children.push({
                                type: 'exponent',
                                version: 1,
                                base: { state: decompressSubstate(node.base) },
                                exp: { state: decompressSubstate(node.exp) },
                            });
                        } else if (node.t === 'root') {
                            children.push({
                                type: 'root',
                                version: 1,
                                radicand: { state: decompressSubstate(node.rad) },
                                index: node.idx ? { state: decompressSubstate(node.idx) } : null,
                            });
                        } else if (node.t === 'overline') {
                            children.push({
                                type: 'overline',
                                version: 1,
                                content: { state: decompressSubstate(node.content) },
                                variant: node.variant || 'segment',
                            });
                        }
                    }
                }

                paragraphs.push({
                    children,
                    direction: null,
                    format: '',
                    indent: 0,
                    type: 'paragraph',
                    version: 1,
                });
            }

            return JSON.stringify({
                root: {
                    children: paragraphs,
                    direction: null,
                    format: '',
                    indent: 0,
                    type: 'root',
                    version: 1,
                },
            });
        }

        if (parsed.root) {
            return compressedStr;
        }

        return emptyState();
    } catch (e) {
        console.error('Error decompressing state:', e);
        return emptyState();
    }
}

function emptyState(): string {
    return JSON.stringify({
        root: {
            children: [
                {
                    children: [],
                    direction: null,
                    format: '',
                    indent: 0,
                    type: 'paragraph',
                    version: 1,
                },
            ],
            direction: null,
            format: '',
            indent: 0,
            type: 'root',
            version: 1,
        },
    });
}

/* ---------------------------------- Examples ---------------------------------- */

/*

// Example with line breaks:
Input in editor:
  test
  
  32%
  3/4

Compressed format:
{
  "nodes": [
    {"nodes": [{"t":"text","c":"test"}]},           ← First line
    {"nodes": []},                                   ← Empty line (blank line)
    {"nodes": [{"t":"text","c":"32%"}]},            ← Third line
    {"nodes": [{"t":"fraction","num":"3","den":"4"}]} ← Fourth line
  ]
}

When decompressed, restores:
- First paragraph: "test"
- Second paragraph: empty (shows as blank line)
- Third paragraph: "32%"
- Fourth paragraph: fraction 3/4

*/
